<template>
	<div class="delivery-options">
		<div class="delivery-options__title-row">
			<h2 class="delivery-options__title">Delivery Options</h2>
		</div>

		<div class="delivery-options__methods">
			<button
				class="delivery-options__method"
				:class="{ 'delivery-options__method--selected': delivery === 'zip' }"
				@click="delivery = 'zip'">
				<span class="delivery-options__method-radio"
					><span class="delivery-options__method-dot"
				/></span>
				<span class="delivery-options__method-label">ZIP Archive</span>
			</button>
			<button
				class="delivery-options__method"
				:class="{ 'delivery-options__method--selected': delivery === 'git' }"
				@click="delivery = 'git'">
				<span class="delivery-options__method-radio"
					><span class="delivery-options__method-dot"
				/></span>
				<span class="delivery-options__method-label">Git Repository</span>
			</button>
		</div>

		<div
			v-if="delivery === 'git'"
			class="delivery-options__repos">
			<div class="delivery-options__repo-header">
				<div class="delivery-options__repo-label">Select repository</div>
				<Button
					v-if="repos.length !== 0"
					class="delivery-options__add"
					icon="fas fa-plus"
					label="Add repository"
					severity="secondary"
					size="small"
					variant="outlined"
					@click="openAddRepoDialog" />
			</div>
			<div
				v-if="repos.length === 0"
				class="delivery-options__empty">
				<div class="delivery-options__empty-text">
					No repositories configured yet. Add a repository to push via Git.
				</div>
				<Button
					class="delivery-options__empty-add"
					icon="fas fa-plus"
					label="Add repository"
					severity="secondary"
					size="small"
					variant="outlined"
					@click="openAddRepoDialog" />
			</div>
			<button
				v-for="repo in repos"
				v-else
				:key="repo.id"
				class="delivery-options__repo"
				:class="{ 'delivery-options__repo--selected': repo.id === selectedRepo }"
				@click="selectedRepo = repo.id">
				<span class="delivery-options__repo-radio">
					<span class="delivery-options__repo-dot" />
				</span>
				<span class="delivery-options__repo-label">{{
					getRepoName(repo.github_repository_url)
				}}</span>
			</button>
		</div>

		<p
			v-if="delivery === 'git'"
			class="delivery-options__hint">
			Code will be delivered to the selected repository as a new branch.
		</p>

		<RepositoryConfigForm
			v-if="props.detailsState.details.value?.id"
			as-dialog
			:force-values="{ app_id: Number(route.params.id) }"
			:hide-inputs="['app_id']"
			:should-redirect="false"
			:visible="repositoryConfigFormOpen"
			@close="onRepoFormClose"
			@created="onRepoCreated"
			@deleted="onRepoDeleted"
			@submit="onRepoFormClose"
			@updated="onRepoUpdated" />
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Button from 'primevue/button'
import { useRoute } from 'vue-router'
import RepositoryConfigForm from '@/views/RepositoryConfig/components/Form.vue'
import type { AppDetailsState } from '@/models/App/States'
import type { RepositoryConfig } from '@/models/RepositoryConfig/Model'

type DeliveryType = 'zip' | 'git'

const emit = defineEmits<{
	(e: 'update:delivery', payload: { type: DeliveryType; repositoryId?: string | null }): void
}>()

const props = defineProps<{ detailsState: AppDetailsState }>()
const route = useRoute()

const delivery = ref<DeliveryType>('zip')
const repos = computed<RepositoryConfig[]>(
	() => props.detailsState.details.value?.repository_configs ?? [],
)
const selectedRepo = ref<RepositoryConfig['id'] | null>(null)

watch(
	() => repos.value,
	(newRepos) => {
		if (!selectedRepo.value && newRepos.length > 0) {
			selectedRepo.value = newRepos[0]!.id
		}
	},
	{ immediate: true },
)

watch(
	[delivery, selectedRepo],
	() => {
		emit('update:delivery', {
			type: delivery.value,
			repositoryId: delivery.value === 'git' ? (selectedRepo.value as any) : null,
		})
	},
	{ immediate: true },
)
function getRepoName(url: string) {
	const match = (url ?? '').match(/github\.com\/(.+)$/)
	return match ? match[1] : url
}

const repositoryConfigFormOpen = ref<boolean>(false)
function openAddRepoDialog() {
	repositoryConfigFormOpen.value = true
}
async function refreshDetails() {
	await props.detailsState.getDetails(route.params.id as string, {
		with: ['latestBuild', 'repositoryConfigs'],
	})
}

function onRepoFormClose() {
	repositoryConfigFormOpen.value = false
}
async function onRepoCreated() {
	await refreshDetails()
	repositoryConfigFormOpen.value = false
	// Prefer the last repo which is most likely the newly created one
	const list = repos.value
	if (list.length) selectedRepo.value = list[list.length - 1]!.id
}
async function onRepoUpdated() {
	await refreshDetails()
}
async function onRepoDeleted() {
	await refreshDetails()
}
</script>

<style scoped lang="scss">
.delivery-options {
	--card-bg: var(--p-surface-900);
	--border: var(--p-surface-800);
	--muted: var(--p-surface-400);
	background: var(--card-bg);
	border: 1px solid var(--border);
	border-radius: 12px;
	padding: 16px;

	.delivery-options__title-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 16px;
	}

	.delivery-options__title {
		font-size: 20px;
		font-weight: 800;
	}

	.delivery-options__methods {
		display: flex;
		gap: 10px;
		margin-bottom: 16px;

		.delivery-options__method {
			flex: 1;
			display: flex;
			gap: 8px;
			align-items: center;
			border: 1px solid var(--border);
			border-radius: 10px;
			padding: 10px 12px;
			cursor: pointer;
			background: transparent;
			text-align: left;

			.delivery-options__method-label {
				font-weight: 600;
			}

			.delivery-options__method-radio {
				width: 16px;
				height: 16px;
				border-radius: 100%;
				border: 1px solid var(--border);
				display: inline-flex;
				align-items: center;
				justify-content: center;
				background: var(--card-bg);
			}

			.delivery-options__method-dot {
				width: 8px;
				height: 8px;
				border-radius: 100%;
				background: transparent;
			}

			&--selected {
				border-color: rgba(238, 255, 1, 0.6);
				background: rgba(238, 255, 1, 0.05);

				.delivery-options__method-dot {
					background: var(--p-yellow-500);
				}
			}
		}
	}

	.delivery-options__repos {
		display: flex;
		flex-direction: column;
		gap: 16px;
		margin-bottom: 16px;

		.delivery-options__repo-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}

		.delivery-options__repo-label {
			font-weight: 600;
		}

		.delivery-options__empty {
			width: 100%;
			flex-direction: column;
			border: 1px dashed var(--border);
			border-radius: 12px;
			padding: 14px;
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 16px;

			.delivery-options__empty-text {
				color: var(--muted);
			}

			.delivery-options__empty-add {
				width: 100%;
			}
		}

		.delivery-options__repo {
			width: 100%;
			background: transparent;
			border: 1px solid var(--border);
			border-radius: 12px;
			padding: 12px;
			display: flex;
			align-items: center;
			gap: 8px;
			cursor: pointer;
			text-align: left;

			&--selected {
				border-color: rgba(238, 255, 1, 0.6);
				background: rgba(238, 255, 1, 0.05);
				box-shadow: 0 0 0 1px rgba(238, 255, 1, 0.4) inset;
			}

			.delivery-options__repo-radio {
				width: 16px;
				height: 16px;
				border-radius: 100%;
				border: 1px solid var(--border);
				display: inline-flex;
				align-items: center;
				justify-content: center;
				background: var(--card-bg);
			}

			.delivery-options__repo-dot {
				width: 8px;
				height: 8px;
				border-radius: 100%;
				background: transparent;
			}

			&--selected .delivery-options__repo-dot {
				background: var(--p-yellow-500);
			}

			.delivery-options__repo-label {
				font-weight: 600;
			}
		}
	}

	.delivery-options__hint {
		margin-top: 6px;
		color: var(--muted);
		font-size: 13px;
	}
}
</style>
